import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Plan {
  id: string
  name: string
  price: number
  currency: string
  interval: string
  features: string[]
  limits: { api_calls: number; users: number; storage_gb: number }
}

interface Subscription {
  id: string
  customer_id: string
  customer_name: string
  plan_id: string
  status: string
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  created_at: string
}

export default function SubscriptionPage() {
  const [plans, setPlans] = useState<Plan[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [usage, setUsage] = useState<any[]>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [planRes, subRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/subscriptions/plans'),
      fetch(API_BASE + '/subscriptions/'),
      fetch(API_BASE + '/subscriptions/statistics')
    ])
    if (planRes.ok) setPlans((await planRes.json()).plans || [])
    if (subRes.ok) setSubscriptions((await subRes.json()).subscriptions || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const viewSubscription = async (sub: Subscription) => {
    setSelectedSubscription(sub)
    const res = await fetch(API_BASE + '/subscriptions/' + sub.id + '/usage')
    if (res.ok) setUsage((await res.json()).usage || [])
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'past_due': return 'bg-red-100 text-red-800'
      case 'canceled': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlanName = (planId: string) => {
    return plans.find(p => p.id === planId)?.name || planId
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-EU', { style: 'currency', currency }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Subscription Management</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Subscriptions</div>
              <div className="text-2xl font-bold">{statistics.total_subscriptions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_subscriptions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">MRR</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.mrr, 'EUR')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Churn Rate</div>
              <div className="text-2xl font-bold text-red-600">{statistics.churn_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Trial Conversion</div>
              <div className="text-2xl font-bold text-green-600">{statistics.trial_conversions}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Value</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.avg_subscription_value, 'EUR')}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-8">
          {plans.map(plan => (
            <div key={plan.id} className={`bg-white rounded-lg shadow p-6 ${plan.name === 'Professional' ? 'border-2 border-blue-500' : ''}`}>
              {plan.name === 'Professional' && <div className="text-blue-600 text-sm font-semibold mb-2">Most Popular</div>}
              <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
              <div className="text-3xl font-bold mb-4">{formatCurrency(plan.price, plan.currency)}<span className="text-sm font-normal text-gray-500">/{plan.interval}</span></div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-green-500">✓</span>{f}
                  </li>
                ))}
              </ul>
              <div className="border-t pt-4 space-y-1 text-sm text-gray-500">
                <div>API Calls: {plan.limits.api_calls === -1 ? 'Unlimited' : plan.limits.api_calls.toLocaleString()}</div>
                <div>Users: {plan.limits.users === -1 ? 'Unlimited' : plan.limits.users}</div>
                <div>Storage: {plan.limits.storage_gb} GB</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Subscriptions</h2></div>
            <div className="divide-y">
              {subscriptions.map(sub => (
                <div key={sub.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => viewSubscription(sub)}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{sub.customer_name}</div>
                      <div className="text-sm text-gray-500">{getPlanName(sub.plan_id)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(sub.status)}`}>{sub.status}</span>
                      {sub.cancel_at_period_end && <span className="text-xs text-red-500">Canceling</span>}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    Period: {new Date(sub.current_period_start).toLocaleDateString()} - {new Date(sub.current_period_end).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            {selectedSubscription ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedSubscription.customer_name}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Plan</span>
                    <span className="font-medium">{getPlanName(selectedSubscription.plan_id)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedSubscription.status)}`}>{selectedSubscription.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Since</span>
                    <span>{new Date(selectedSubscription.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {usage.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-semibold mb-3">Usage</h4>
                    {usage.map((u, i) => (
                      <div key={i} className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize">{u.metric.replace('_', ' ')}</span>
                          <span>{u.limit === -1 ? 'Unlimited' : `${u.used.toLocaleString()} / ${u.limit.toLocaleString()}`}</span>
                        </div>
                        {u.limit !== -1 && (
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${u.used / u.limit > 0.8 ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${Math.min((u.used / u.limit) * 100, 100)}%` }}></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Subscription Details</h3>
                <p className="text-gray-500 text-sm">Select a subscription to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
